import { useMemo } from 'react';
import { AlertTriangle, Euro, Home, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { useCommissions, type Comision } from '../api/commissions';
import { CONTRATO_LABEL, type Contrato } from '../lib/types';
import { AyudaGuia } from '../components/Ayuda';

const eur = (n: number) => `${n.toLocaleString('es-ES')} €`;

// AAAA-MM → "agosto 2026". Se construye por partes para no depender de la zona
// horaria (new Date('2026-08') se interpreta en UTC y puede saltar de mes).
function mesLabel(mes: string): string {
  const [y, m] = mes.split('-').map(Number);
  if (!y || !m) return mes;
  const s = new Date(y, m - 1, 1).toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const mesActual = () => new Date().toISOString().slice(0, 7);

export default function ComisionesView() {
  const { data: comisiones, isLoading, isError, error, refetch } = useCommissions();

  const { total, esteMes, porOrigen, meses } = useMemo(() => {
    const items = comisiones ?? [];
    const suma = (xs: Comision[]) => xs.reduce((s, c) => s + (c.importe ?? 0), 0);
    const now = mesActual();

    // Agrupa por mes (ya vienen ordenadas por fecha desc; conservamos ese orden).
    const porMes = new Map<string, Comision[]>();
    for (const c of items) {
      const arr = porMes.get(c.mes) ?? [];
      arr.push(c);
      porMes.set(c.mes, arr);
    }
    const meses = Array.from(porMes.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([mes, lista]) => ({ mes, lista, total: suma(lista) }));

    return {
      total: suma(items),
      esteMes: suma(items.filter((c) => c.mes === now)),
      porOrigen: {
        operacion: suma(items.filter((c) => c.origen === 'operacion')),
        servicio: suma(items.filter((c) => c.origen === 'servicio')),
      },
      meses,
    };
  }, [comisiones]);

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-deep">Comisiones</h1>
        <AyudaGuia seccion="comisiones" />
      </div>
      <p className="mt-1 text-sm text-ink/55">
        Lo que ha generado la agencia, por mes y por origen. Se registran solas al
        cerrar una operación o al anotar la comisión de un servicio.
      </p>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Total" value={eur(total)} />
        <Kpi label="Este mes" value={eur(esteMes)} />
        <Kpi label="Operaciones" value={eur(porOrigen.operacion)} />
        <Kpi label="Servicios" value={eur(porOrigen.servicio)} />
      </div>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError ? (
        <div className="mt-8 rounded-2xl border border-dashed border-red-300 bg-red-50 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto text-red-500" size={28} strokeWidth={1.5} />
          <p className="mt-3 font-bold text-red-700">No se han podido cargar las comisiones</p>
          <p className="mt-1 text-sm text-red-600/80">
            Es un fallo del servidor, no es que no haya ninguna. {(error as Error)?.message}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      ) : meses.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-deep/20 bg-surface px-6 py-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-leaf/40 text-deep">
            <Euro size={22} strokeWidth={1.75} />
          </div>
          <p className="mt-4 font-bold text-deep">Todavía no hay comisiones</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-ink/60">
            Aparecerán aquí en cuanto cierres una operación en Operaciones o registres
            la comisión de un servicio en una solicitud.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {meses.map(({ mes, lista, total }) => (
            <section key={mes}>
              <div className="flex items-baseline justify-between border-b border-deep/10 pb-2">
                <h2 className="text-sm font-bold uppercase tracking-wider text-ink/55">
                  {mesLabel(mes)}
                </h2>
                <span className="text-sm font-extrabold text-deep">{eur(total)}</span>
              </div>
              <ul className="mt-3 space-y-2">
                {lista.map((c) => (
                  <FilaComision key={c.commission_id} c={c} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function FilaComision({ c }: { c: Comision }) {
  const esOperacion = c.origen === 'operacion';
  return (
    <li className="flex items-center gap-3 rounded-2xl border border-deep/10 bg-surface px-4 py-3">
      <span
        className={clsx(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
          esOperacion ? 'bg-brand/15 text-brand' : 'bg-leaf/40 text-deep',
        )}
      >
        {esOperacion ? <Home size={16} /> : <Sparkles size={16} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-deep">{c.concepto || 'Comisión'}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink/55">
          <span
            className={clsx(
              'rounded-full px-2 py-0.5 font-bold uppercase tracking-wide',
              esOperacion ? 'bg-brand/10 text-brand' : 'bg-leaf/50 text-deep',
            )}
          >
            {esOperacion ? 'Operación' : 'Servicio'}
          </span>
          {esOperacion && c.contrato && (
            <span>{CONTRATO_LABEL[c.contrato as Contrato] ?? c.contrato}</span>
          )}
        </div>
      </div>
      <span className="shrink-0 text-sm font-extrabold text-deep">{eur(c.importe ?? 0)}</span>
    </li>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-deep/10 bg-surface px-4 py-3">
      <p className="text-2xl font-extrabold text-deep">{value}</p>
      <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{label}</p>
    </div>
  );
}
